import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { StoreLayoutModal } from './StoreLayoutModal';
import { dataModel } from './dataModel';
import { getLocalStore, isCompatibleProductType, returnObject, transformData, parseStringConfig, shouldExcludeProduct } from '../helper';
import { AisleBayInventory } from './AisleBayInventory';

const { string, bool, shape } = proptypes;

const AisleBaySingleItem = ({
  itemId, store, channel, centered, clsRemediation
}) => {
  const storeId = store?.storeId;
  const storeName = store?.storeName;
  const isLocalized = store?.isLocalized;
  const isMobile = channel === 'mobile';
  const { placeholders, preservePlaceholders } = returnObject(clsRemediation);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const { isConsumerApp } = useContext(ExperienceContext);

  // request simple metadata for ssr
  const { data: metadata } = useDataModel('product', {
    variables: { itemId }
  });

  const { data: storeDetailsData } = useDataModel('storeDetails', {
    skip: !isConsumerApp,
    variables: { storeId }
  });

  const { availabilityType = {}, identifiers = {}, media = {} } = returnObject(metadata?.product);
  const { storeSkuNumber, productType = '', brandName, productLabel } = returnObject(identifiers);
  const { type = '' } = returnObject(availabilityType);
  const isOnlineOnly = /Online/i.test(type);
  const { url } = returnObject(media?.image, true);
  const isAppliance = /MAJOR_APPLIANCE/i.test(productType);

  let zipCode = store?.storeZip;
  if (typeof window !== 'undefined') {
    const zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    zipCode = zipDetails?.zipcode;
  }
  // request fulfillment on client for inventory
  const { data: fulfillmentData = { product: {} }, loading } = useDataModel('clientOnlyProduct', {
    variables: { itemId, storeId, zipCode },
    skip: !isCompatibleProductType({ isAppliance, isOnlineOnly, storeSkuNumber }) || !isLocalized,
    ssr: false
  });

  const excludedProducts = useConfigService('aisle-bay-exclusion-list');
  const configValue = parseStringConfig(excludedProducts);
  const excludeProduct = shouldExcludeProduct(metadata?.product?.info || {}, configValue);

  const fulfillment = fulfillmentData?.product?.fulfillment;
  const { fulfillmentOptions } = returnObject(fulfillment);
  const dataIsLocalized = Boolean(isLocalized && !loading && fulfillment);
  const isActiveSku = fulfillment?.anchorStoreStatus
    && ((/(active)|(in_season)/i.test(fulfillment?.anchorStoreStatusType)) || !excludeProduct);
  const localStore = getLocalStore(fulfillmentOptions);
  const isInStore = isActiveSku && (localStore?.inventory?.quantity || isAppliance);
  const {
    street, city, state, country, postalCode
  } = returnObject(storeDetailsData?.storeDetails?.address);
  const { isLimitedQuantity, isInStock, quantity } = returnObject(localStore?.inventory);

  // if in store, then get aisle bay info
  const { data: aisleBayData } = useDataModel('aislebay', {
    skip: !isInStore,
    ssr: false,
    variables: {
      storeId,
      storeSkuIds: storeSkuNumber ? [storeSkuNumber] : []
    }
  });

  const aisleBayInfo = aisleBayData?.aislebay?.storeSkus?.[0]?.aisleBayInfo;
  const isAisleBayEmpty = aisleBayData && !aisleBayInfo;
  const { aisle, bay } = returnObject(aisleBayInfo);

  const {
    hasAllRequiredProperties,
    aisleBayInventory,
    localStoreName,
    aisleBayMessage
  } = transformData(localStore, isAppliance, aisleBayInfo, storeName);

  // data invalidation
  const shouldReturnNull = !hasAllRequiredProperties
    && (
      // --> may cause cls from subsequent content insertion | ('product') --> 0 CLS
      (!placeholders || !isCompatibleProductType)
      // --> may cause cls from collapse of early placeholders
      || (!preservePlaceholders && (
        // ('clientOnlyProduct') | ('aislebay')
        (dataIsLocalized && !isInStore) || isAisleBayEmpty)
      )
    );

  if (shouldReturnNull) {
    return null;
  }

  const aisleBayClasses = classNames('sui-leading-loose sui-text-base', {
    'sui-invisible': !hasAllRequiredProperties,
    'sui-m-auto sui-flex sui-justify-center sui-text-center': centered
  });

  const onStoreLayoutClick = () => {
    if (isConsumerApp) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('NATIVE_APP.STORE_MAP', {
        storeId,
        storeName,
        street,
        city,
        state,
        country,
        postalCode,
        sku: storeSkuNumber,
        aisle,
        bay,
        friendlyAisleDescription: aisleBayInfo?.invLocDescFriendly,
        imageURL: url,
        quantity,
        storePhone: localStore?.storePhone,
        isLimitedStock: isLimitedQuantity,
        brandName,
        productLabel,
        stockStatus: isInStock
      });
    } else { setStoreModalOpen(true); }
  };

  return (
    <fieldset className={aisleBayClasses} data-component="AisleBaySingleItem">
      <legend className="sui-flex sui-pb-1">
        <span className="sui-mr-2 sui-font-bold">
          <>{localStoreName} Store</>
        </span>
        {(isMobile) && <AisleBayInventory aisleBayInventory={aisleBayInventory} />}
      </legend>
      <section className="sui-flex sui-items-center">
        {(!isMobile) && <AisleBayInventory aisleBayInventory={aisleBayInventory} />}
        {!isAppliance && (
          <>
            <span className="sui-flex sui-mr-2">{aisleBayMessage}</span>
            {isMobile && (
              <>
                <button
                  type="button"
                  className="sui-underline sui-text-primary"
                  onClick={onStoreLayoutClick}
                >
                  Store Map
                </button>
                <StoreLayoutModal
                  open={storeModalOpen}
                  onClose={() => setStoreModalOpen(false)}
                  storeId={storeId}
                  storeName={storeName}
                  aisle={aisle}
                  bay={bay}
                />
              </>
            )}
          </>
        )}
      </section>
    </fieldset>
  );
};

AisleBaySingleItem.displayName = 'AisleBaySingleItem';

AisleBaySingleItem.propTypes = {
  itemId: string,
  store: shape({
    storeId: string,
    storeName: string
  }).isRequired,
  channel: string.isRequired,
  // justifies and aligns all text centered
  centered: bool,
  clsRemediation: shape({
    // reserves whitespace while loading
    placeholders: bool,
    // disallows collapse of empty whitespace
    preservePlaceholders: bool
  }).isRequired
};

AisleBaySingleItem.defaultProps = {
  itemId: null,
  centered: false
};

AisleBaySingleItem.dataModel = extend({}, dataModel);

export { AisleBaySingleItem };
